import React, { useState, useRef, useEffect } from 'react';
import { Globe, X } from 'lucide-react';
import { useTranslateUI } from '../contexts/TranslationContext';

/**
 * Globaler Live-Übersetzungs-Button.
 *
 * Floating bottom-right circle (Weltkugel). Click → expandiert ein kleines
 * Panel mit ON/OFF-Switch und Sprach-Picker. Nutzt den TranslationContext —
 * Toggle wirkt UI-weit (Profil, Match-Inbox, Chat). Der Orb ist explizit
 * ausgenommen (Voice-Befehle bleiben in der Originalsprache).
 */
export const GlobalTranslateButton = () => {
  const { translateOn, target, setTranslateOn, setTarget, active } = useTranslateUI();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Hide entirely on the Orb route — voice commands must not be translated
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/orb-app') || path === '/orb' || path === '/orb-login') return null;

  const langs = [
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { code: 'pl', label: 'Polski', flag: '🇵🇱' },
    { code: 'uk', label: 'Українська', flag: '🇺🇦' },
  ];

  return (
    <div ref={ref} data-testid="global-translate" style={st.wrapper}>
      {open && (
        <div data-testid="global-translate-panel" style={st.panel}>
          <div style={st.header}>
            <Globe size={16} color="#00d4ff" />
            <strong style={{ flex: 1, fontSize: 14, color: '#f1f5f9' }}>Live-Übersetzung</strong>
            <button data-testid="global-translate-close" onClick={() => setOpen(false)} style={st.close}>
              <X size={14} />
            </button>
          </div>

          <label data-testid="global-translate-toggle-row" style={st.row}>
            <span style={{ fontSize: 13, color: '#cbd5e1' }}>
              {translateOn ? 'AN — alles wird übersetzt' : 'AUS'}
            </span>
            <button data-testid="global-translate-toggle" type="button"
              onClick={() => setTranslateOn(!translateOn)}
              style={{ ...st.switch, background: translateOn ? '#00d4ff' : 'rgba(255,255,255,0.15)' }}>
              <span style={{ ...st.knob, transform: translateOn ? 'translateX(20px)' : 'translateX(0)' }} />
            </button>
          </label>

          {translateOn && (
            <div style={st.langRow} data-testid="global-translate-targets">
              {langs.map(l => (
                <button key={l.code} type="button"
                  data-testid={`global-translate-target-${l.code}`}
                  onClick={() => setTarget(l.code)}
                  style={{
                    ...st.langBtn,
                    background: target === l.code ? 'rgba(0,212,255,0.18)' : 'transparent',
                    borderColor: target === l.code ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.08)',
                  }}>
                  <span style={{ fontSize: 18 }}>{l.flag}</span>
                  <span style={{ fontSize: 11, color: target === l.code ? '#00d4ff' : '#cbd5e1' }}>
                    {l.code.toUpperCase()}
                  </span>
                </button>
              ))}
            </div>
          )}

          {!active && (
            <div data-testid="global-translate-inactive" style={st.warn}>
              DeepL gerade nicht erreichbar — Übersetzung pausiert.
            </div>
          )}
        </div>
      )}

      <button
        data-testid="global-translate-btn"
        onClick={() => setOpen(o => !o)}
        title="Live-Übersetzung"
        style={{
          ...st.fab,
          background: translateOn
            ? 'linear-gradient(135deg,#00d4ff,#0ea5e9)'
            : 'rgba(15,23,42,0.92)',
          borderColor: translateOn ? 'rgba(0,212,255,0.6)' : 'rgba(255,255,255,0.15)',
        }}>
        <Globe size={22} color={translateOn ? '#fff' : '#cbd5e1'} />
        {translateOn && (
          <span data-testid="global-translate-target-badge" style={st.badge}>
            {target.toUpperCase()}
          </span>
        )}
      </button>
    </div>
  );
};


const st = {
  // SOS-Button sitzt bei right:18, bottom:22 (64px hoch), SOS-Hint bei bottom:96.
  // → Globe-FAB darüber stapeln, damit er nicht überlappt.
  wrapper: { position: 'fixed', bottom: 100, right: 22, zIndex: 9998 },
  fab: {
    width: 56, height: 56, borderRadius: '50%',
    border: '2px solid', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)', position: 'relative',
    transition: 'all 0.2s',
  },
  badge: {
    position: 'absolute', bottom: -4, right: -4,
    background: '#0f172a', color: '#00d4ff', fontSize: 9, fontWeight: 700,
    padding: '2px 5px', borderRadius: 6, border: '1px solid rgba(0,212,255,0.5)',
    minWidth: 22, textAlign: 'center',
  },
  panel: {
    position: 'absolute', bottom: 70, right: 0, width: 300,
    background: 'rgba(15,23,42,0.98)', backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14,
    padding: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
  },
  header: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 },
  close: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: '#94a3b8', padding: 4, borderRadius: 6,
  },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0' },
  switch: {
    width: 44, height: 24, borderRadius: 999, position: 'relative',
    cursor: 'pointer', border: 'none', padding: 0, transition: 'background 0.15s',
  },
  knob: {
    width: 20, height: 20, borderRadius: '50%', background: '#fff',
    position: 'absolute', top: 2, left: 2, transition: 'transform 0.18s',
    boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
  },
  langRow: {
    marginTop: 12, display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)', gap: 6,
  },
  langBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '6px 4px', borderRadius: 8, cursor: 'pointer',
    border: '1px solid', transition: 'all 0.15s',
  },
  warn: {
    marginTop: 12, padding: 8, fontSize: 11, borderRadius: 8,
    background: 'rgba(245,158,11,0.12)', color: '#fbbf24',
    border: '1px solid rgba(245,158,11,0.3)',
  },
};
